import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, LogOut, Upload, Star, Car, Calendar, HandCoins, Mail, Image as ImageIcon } from "lucide-react";
import { api, apiError, formatPrice, formatMiles, imgSrc } from "@/lib/api";
import { GALLERY_CATEGORIES } from "@/lib/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const inputCls =
  "h-11 w-full rounded-none border-white/15 bg-black/40 px-4 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue";
const selectCls = "h-11 w-full border border-white/15 bg-black/40 px-4 text-sm text-zinc-300 outline-none focus:border-trx-blue";

const EMPTY_FORM = {
  year: "", make: "", model: "", trim: "", body_type: "Sedan", status: "Available",
  price: "", mileage: "", description: "", images: [], featured: false,
};

const VehicleForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const isEdit = Boolean(initial?.id);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadFiles = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      }
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(apiError(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.images.length) {
      toast.error("Add at least one photo.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      year: Number(form.year),
      price: Number(form.price),
      mileage: Number(form.mileage),
    };
    try {
      if (isEdit) {
        await api.put(`/vehicles/${form.id}`, payload);
      } else {
        await api.post("/vehicles", payload);
      }
      toast.success(isEdit ? "Vehicle updated." : "Vehicle added.");
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-black/85 backdrop-blur-sm" data-testid="vehicle-form-modal">
      <div className="mx-auto my-10 max-w-3xl border border-white/10 bg-[#0a0a0a] p-8 md:p-10">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl font-bold text-white">{isEdit ? "Edit Vehicle" : "Add Vehicle"}</h2>
          <button onClick={onClose} data-testid="vehicle-form-close" className="border border-white/15 p-2 text-zinc-400 hover:text-white" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={save} className="mt-8 space-y-5" data-testid="vehicle-form">
          <div className="grid gap-4 md:grid-cols-4">
            <Input required type="number" placeholder="Year *" value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} data-testid="vf-year" />
            <Input required placeholder="Make *" value={form.make} onChange={(e) => set("make", e.target.value)} className={inputCls} data-testid="vf-make" />
            <Input required placeholder="Model *" value={form.model} onChange={(e) => set("model", e.target.value)} className={inputCls} data-testid="vf-model" />
            <Input placeholder="Trim" value={form.trim} onChange={(e) => set("trim", e.target.value)} className={inputCls} data-testid="vf-trim" />
          </div>
          <div className="grid gap-4 md:grid-cols-4">
            <select value={form.body_type} onChange={(e) => set("body_type", e.target.value)} className={selectCls} data-testid="vf-body-type">
              {["Coupe", "Sedan", "SUV", "Truck"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className={selectCls} data-testid="vf-status">
              <option>Available</option>
              <option>Sold</option>
            </select>
            <Input required type="number" placeholder="Price ($) *" value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} data-testid="vf-price" />
            <Input required type="number" placeholder="Mileage *" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} className={inputCls} data-testid="vf-mileage" />
          </div>
          <Textarea
            placeholder="Vehicle description"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            className="min-h-[100px] w-full rounded-none border-white/15 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue"
            data-testid="vf-description"
          />
          <label className="flex cursor-pointer items-center gap-3 text-sm text-zinc-300">
            <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="h-4 w-4 accent-[#0055ff]" data-testid="vf-featured" />
            <Star className="h-4 w-4 text-trx-blue" /> Featured on homepage
          </label>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Photos</p>
            <div className="flex flex-wrap gap-3">
              {form.images.map((src, i) => (
                <div key={i} className="group relative aspect-[16/10] w-32 overflow-hidden border border-white/10">
                  <img src={imgSrc(src)} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => set("images", form.images.filter((_, j) => j !== i))}
                    data-testid={`vf-remove-image-${i}`}
                    className="absolute right-1 top-1 bg-black/80 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    aria-label="Remove photo"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="vf-upload-btn"
                className="flex aspect-[16/10] w-32 flex-col items-center justify-center gap-2 border border-dashed border-white/20 text-zinc-500 transition-colors hover:border-trx-blue hover:text-trx-blue disabled:opacity-50"
              >
                <Upload className="h-5 w-5" />
                <span className="text-[10px] uppercase tracking-wider">{uploading ? "Uploading…" : "Upload"}</span>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                data-testid="vf-file-input"
                onChange={(e) => {
                  if (e.target.files?.length) uploadFiles([...e.target.files]);
                  e.target.value = "";
                }}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving || uploading}
              data-testid="vf-save"
              className="bg-trx-blue px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-white hover:text-black disabled:opacity-50"
            >
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Vehicle"}
            </button>
            <button type="button" onClick={onClose} className="border border-white/20 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 hover:text-white">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SubmissionCard = ({ children }) => (
  <div className="border border-white/10 bg-card p-5 text-sm">{children}</div>
);

const Field = ({ label, value }) =>
  value ? (
    <p className="mt-1.5 text-zinc-400">
      <span className="text-zinc-600">{label}: </span>
      {value}
    </p>
  ) : null;

const TABS = [
  { key: "inventory", label: "Inventory", icon: Car },
  { key: "test_drives", label: "Test Drives", icon: Calendar },
  { key: "appointments", label: "Appointments", icon: Calendar },
  { key: "financing", label: "Financing", icon: HandCoins },
  { key: "contacts", label: "Messages", icon: Mail },
  { key: "reviews", label: "Reviews", icon: Star },
  { key: "gallery", label: "Gallery", icon: ImageIcon },
];

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("inventory");
  const [vehicles, setVehicles] = useState([]);
  const [subs, setSubs] = useState({ appointments: [], test_drives: [], financing: [], contacts: [] });
  const [reviews, setReviews] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryForm, setGalleryForm] = useState({ category: GALLERY_CATEGORIES[0], label: "" });
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [editGalleryId, setEditGalleryId] = useState(null);
  const [editGalleryForm, setEditGalleryForm] = useState({ label: "", category: "" });
  const galleryFileRef = useRef(null);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(undefined);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [v, s, rv, g] = await Promise.all([
        api.get("/vehicles"),
        api.get("/admin/submissions"),
        api.get("/admin/reviews"),
        api.get("/gallery"),
      ]);
      setVehicles(v.data);
      setSubs(s.data);
      setReviews(rv.data);
      setGalleryItems(g.data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  };

  useEffect(() => {
    document.title = "Dashboard | TRX Admin";
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        load();
      })
      .catch(() => navigate("/admin"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    localStorage.removeItem("trx_token");
    navigate("/admin");
  };

  const toggleStatus = async (v) => {
    const next = v.status === "Sold" ? "Available" : "Sold";
    try {
      await api.put(`/vehicles/${v.id}`, { status: next });
      toast.success(`Marked as ${next}.`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete ${v.year} ${v.make} ${v.model}? This cannot be undone.`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success("Vehicle deleted.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleSelect = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} selected item(s)? This cannot be undone.`)) return;
    try {
      await api.post("/admin/submissions/bulk-delete", { collection: tab, ids: [...selected] });
      toast.success("Deleted selected items.");
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleReview = async (r) => {
    try {
      await api.put(`/admin/reviews/${r.id}`);
      toast.success(r.approved ? "Review unpublished." : "Review is now live.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const deleteReview = async (r) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Review deleted.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const uploadGalleryPhoto = async (file) => {
    setGalleryUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/gallery", { image: data.url, category: galleryForm.category, label: galleryForm.label });
      toast.success("Photo added to gallery.");
      setGalleryForm({ category: GALLERY_CATEGORIES[0], label: "" });
      load();
    } catch (err) {
      toast.error(apiError(err, "Upload failed"));
    } finally {
      setGalleryUploading(false);
    }
  };

  const saveGalleryEdit = async (id) => {
    try {
      await api.put(`/gallery/${id}`, editGalleryForm);
      toast.success("Gallery photo updated.");
      setEditGalleryId(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeGalleryItem = async (item) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await api.delete(`/gallery/${item.id}`);
      toast.success("Photo removed.");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!user) return <div className="min-h-screen bg-trx-black" data-testid="admin-loading" />;

  const counts = {
    inventory: vehicles.length,
    test_drives: subs.test_drives.length,
    appointments: subs.appointments.length,
    financing: subs.financing.length,
    contacts: subs.contacts.length,
    reviews: reviews.length,
    gallery: galleryItems.length,
  };

  const currentItems = subs[tab] || [];

  return (
    <div className="min-h-screen bg-trx-black" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl font-black tracking-tight text-white">TRX</span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-zinc-500">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-5">
            <span className="hidden text-xs text-zinc-500 md:block">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-400 transition-colors hover:border-red-500/60 hover:text-red-400">
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 py-8">
        <div className="mb-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setSelected(new Set()); }}
              data-testid={`admin-tab-${t.key}`}
              className={`flex items-center gap-2 border px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-all ${
                tab === t.key ? "border-trx-blue bg-trx-blue text-white" : "border-white/15 text-zinc-400 hover:text-white"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
              <span className={`ml-1 px-1.5 text-[10px] ${tab === t.key ? "bg-white/20" : "bg-white/10"}`}>{counts[t.key]}</span>
            </button>
          ))}
        </div>

        {tab === "inventory" && (
          <div data-testid="admin-inventory-tab">
            <div className="mb-6 flex items-center justify-between">
              <p className="text-sm text-zinc-500">{vehicles.length} vehicles in inventory</p>
              <button
                onClick={() => setEditing(null)}
                data-testid="add-vehicle-btn"
                className="flex items-center gap-2 bg-trx-blue px-5 py-3 text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-white hover:text-black"
              >
                <Plus className="h-4 w-4" /> Add Vehicle
              </button>
            </div>

            <div className="overflow-x-auto border border-white/10">
              <table className="w-full text-left text-sm" data-testid="vehicles-table">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    <th className="p-4">Vehicle</th>
                    <th className="p-4">Body</th>
                    <th className="p-4">Price</th>
                    <th className="p-4">Mileage</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Featured</th>
                    <th className="p-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v) => (
                    <tr key={v.id} className="border-b border-white/5 transition-colors hover:bg-white/[0.03]" data-testid={`vehicle-row-${v.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img src={imgSrc(v.images?.[0])} alt="" className="h-11 w-16 border border-white/10 object-cover" />
                          <div>
                            <p className="font-semibold text-white">{v.year} {v.make} {v.model}</p>
                            {v.trim && <p className="text-xs text-zinc-500">{v.trim}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-zinc-400">{v.body_type}</td>
                      <td className="p-4 text-white">{formatPrice(v.price)}</td>
                      <td className="p-4 text-zinc-400">{formatMiles(v.mileage)}</td>
                      <td className="p-4">
                        <button
                          onClick={() => toggleStatus(v)}
                          data-testid={`status-toggle-${v.id}`}
                          className={`border px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                            v.status === "Sold"
                              ? "border-white/40 bg-white text-black hover:bg-transparent hover:text-white"
                              : "border-trx-blue/60 text-trx-blue hover:bg-trx-blue hover:text-white"
                          }`}
                        >
                          {v.status || "Available"}
                        </button>
                      </td>
                      <td className="p-4">
                        {v.featured ? <Star className="h-4 w-4 fill-trx-blue text-trx-blue" /> : <span className="text-zinc-700">—</span>}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => setEditing(v)} data-testid={`edit-vehicle-${v.id}`} aria-label="Edit" className="border border-white/15 p-2 text-zinc-400 transition-colors hover:border-trx-blue hover:text-white">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => remove(v)} data-testid={`delete-vehicle-${v.id}`} aria-label="Delete" className="border border-white/15 p-2 text-zinc-400 transition-colors hover:border-red-500/60 hover:text-red-400">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {["test_drives", "appointments", "financing", "contacts"].includes(tab) && (
          <div data-testid={`admin-${tab}`}>
            <div className="mb-5 flex items-center justify-between">
              <label className="flex cursor-pointer items-center gap-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[#0055ff]"
                  checked={currentItems.length > 0 && selected.size === currentItems.length}
                  onChange={(e) => setSelected(e.target.checked ? new Set(currentItems.map((s) => s.id)) : new Set())}
                  data-testid="select-all-checkbox"
                />
                Select All ({selected.size} selected)
              </label>
              <button
                onClick={deleteSelected}
                disabled={selected.size === 0}
                data-testid="delete-selected-btn"
                className="flex items-center gap-2 border border-red-500/50 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-red-400 transition-colors hover:bg-red-500 hover:text-white disabled:opacity-40"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete Selected
              </button>
            </div>
            {currentItems.length === 0 && <p className="text-sm text-zinc-500">Nothing here yet.</p>}
            <div className="grid gap-4 md:grid-cols-2">
              {currentItems.map((s) => (
                <div key={s.id} className={`border bg-card p-5 text-sm transition-colors ${selected.has(s.id) ? "border-trx-blue" : "border-white/10"}`} data-testid={`submission-${s.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-display text-lg font-bold text-white">{s.name}</p>
                      <p className="mt-1 text-trx-blue">{s.vehicle_name || s.service || s.vehicle_interest || "Message"}</p>
                    </div>
                    <input
                      type="checkbox"
                      className="mt-1.5 h-4 w-4 shrink-0 accent-[#0055ff]"
                      checked={selected.has(s.id)}
                      onChange={() => toggleSelect(s.id)}
                      data-testid={`select-${s.id}`}
                      aria-label={`Select ${s.name}`}
                    />
                  </div>
                  <Field label="Phone" value={s.phone} />
                  <Field label="Email" value={s.email} />
                  <Field label="Vehicle" value={s.vehicle} />
                  <Field label="Preferred date" value={s.preferred_date} />
                  <Field label="Down payment" value={s.down_payment} />
                  <Field label="Monthly income" value={s.monthly_income} />
                  <Field label="Message" value={s.message} />
                  <p className="mt-3 text-xs text-zinc-600">{new Date(s.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "reviews" && (
          <div data-testid="admin-reviews">
            {reviews.length === 0 && <p className="text-sm text-zinc-500">No reviews yet.</p>}
            <div className="grid gap-4 md:grid-cols-2">
              {reviews.map((r) => (
                <div key={r.id} className="border border-white/10 bg-card p-5 text-sm" data-testid={`review-${r.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-display text-lg font-bold text-white">{r.name}</p>
                      <p className="mt-0.5 text-xs text-zinc-500">{r.vehicle || "Customer"} · {"★".repeat(r.rating || 5)}</p>
                    </div>
                    <span className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${r.approved ? "bg-trx-blue/20 text-trx-blue" : "bg-white/10 text-zinc-400"}`}>
                      {r.approved ? "Live" : "Pending"}
                    </span>
                  </div>
                  <p className="mt-3 text-zinc-300">“{r.quote}”</p>
                  <div className="mt-4 flex gap-2">
                    <button onClick={() => toggleReview(r)} data-testid={`approve-review-${r.id}`} className="border border-white/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:border-trx-blue hover:text-white">
                      {r.approved ? "Unpublish" : "Approve"}
                    </button>
                    <button onClick={() => deleteReview(r)} data-testid={`delete-review-${r.id}`} className="border border-white/15 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-red-400 transition-colors hover:border-red-500">
                      Delete
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-zinc-600">{new Date(r.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}


        {tab === "gallery" && (
          <div data-testid="admin-gallery">
            <div className="mb-8 flex flex-col gap-4 border border-white/10 bg-card p-5 md:flex-row md:items-end" data-testid="gallery-add-form">
              <div className="flex-1">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Category</p>
                <select value={galleryForm.category} onChange={(e) => setGalleryForm((f) => ({ ...f, category: e.target.value }))} className={selectCls} data-testid="gallery-add-category">
                  {GALLERY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex-1">
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Label (optional)</p>
                <Input placeholder="e.g. M4 Delivery Day" value={galleryForm.label} onChange={(e) => setGalleryForm((f) => ({ ...f, label: e.target.value }))} className={inputCls} data-testid="gallery-add-label" />
              </div>
              <button
                onClick={() => galleryFileRef.current?.click()}
                disabled={galleryUploading}
                data-testid="gallery-upload-btn"
                className="flex items-center justify-center gap-2 bg-trx-blue px-6 py-3 text-xs font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-white hover:text-black disabled:opacity-50"
              >
                <Upload className="h-4 w-4" /> {galleryUploading ? "Uploading…" : "Upload Photo"}
              </button>
              <input
                ref={galleryFileRef}
                type="file"
                accept="image/*"
                hidden
                data-testid="gallery-file-input"
                onChange={(e) => {
                  if (e.target.files?.[0]) uploadGalleryPhoto(e.target.files[0]);
                  e.target.value = "";
                }}
              />
            </div>

            {galleryItems.length === 0 && <p className="text-sm text-zinc-500">No gallery photos yet. Upload your first one above.</p>}
            <div className="grid gap-5 md:grid-cols-3 lg:grid-cols-4">
              {galleryItems.map((item) => (
                <div key={item.id} className="border border-white/10 bg-card" data-testid={`gallery-item-${item.id}`}>
                  <img src={imgSrc(item.image)} alt={item.label || "Gallery photo"} className="aspect-[16/10] w-full object-cover" />
                  {editGalleryId === item.id ? (
                    <div className="space-y-3 p-4">
                      <Input value={editGalleryForm.label} onChange={(e) => setEditGalleryForm((f) => ({ ...f, label: e.target.value }))} className={inputCls} data-testid="gallery-edit-label" />
                      <select value={editGalleryForm.category} onChange={(e) => setEditGalleryForm((f) => ({ ...f, category: e.target.value }))} className={selectCls} data-testid="gallery-edit-category">
                        {GALLERY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                      </select>
                      <div className="flex gap-2">
                        <button onClick={() => saveGalleryEdit(item.id)} data-testid="gallery-edit-save" className="bg-trx-blue px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black">
                          Save
                        </button>
                        <button onClick={() => setEditGalleryId(null)} className="border border-white/20 px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-400 hover:text-white">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4">
                      <p className="font-semibold text-white">{item.label || "Untitled"}</p>
                      <p className="mt-1 text-xs text-zinc-500">{item.category}</p>
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => { setEditGalleryId(item.id); setEditGalleryForm({ label: item.label, category: item.category }); }}
                          data-testid={`gallery-edit-${item.id}`}
                          aria-label="Edit photo"
                          className="border border-white/15 p-2 text-zinc-400 transition-colors hover:border-trx-blue hover:text-white"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => removeGalleryItem(item)}
                          data-testid={`gallery-delete-${item.id}`}
                          aria-label="Delete photo"
                          className="border border-white/15 p-2 text-zinc-400 transition-colors hover:border-red-500/60 hover:text-red-400"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editing !== undefined && (
        <VehicleForm initial={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />
      )}
    </div>
  );
}
